import { LOCALE_STORAGE_KEYS } from 'common/constants/localeStorageConstants'
import { UtilService } from 'common/services/utilService'

export type IntakeLock = {
  patientId: number
  encounterUid: string | null
  userId: number | null
}

export const INTAKE_LOCK_EVENT = 'intake-lock-change'

const emitChange = (): void => {
  try {
    window.dispatchEvent(new Event(INTAKE_LOCK_EVENT))
  } catch {
    // Dispatch is best-effort; consumers re-sync on their next read anyway.
  }
}

const getCurrentUserId = (): number | null => {
  try {
    const raw = window.localStorage.getItem(LOCALE_STORAGE_KEYS.USER)
    if (!raw) return null
    const user = UtilService.safeParseJSON<{ id?: number } | null>(raw, null)
    return typeof user?.id === 'number' ? user.id : null
  } catch {
    return null
  }
}

const isIntakeLock = (value: unknown): value is IntakeLock => {
  if (typeof value !== 'object' || value === null) return false
  const record = value as Record<string, unknown>
  return (
    typeof record.patientId === 'number' &&
    (record.encounterUid === null || typeof record.encounterUid === 'string') &&
    (record.userId === null || typeof record.userId === 'number')
  )
}

const removeStored = (): void => {
  try {
    window.localStorage.removeItem(LOCALE_STORAGE_KEYS.INTAKE_LOCK)
  } catch {
    // Storage can fail (private mode, quota); ignore.
  }
}

export const getIntakeLock = (): IntakeLock | null => {
  try {
    const raw = window.localStorage.getItem(LOCALE_STORAGE_KEYS.INTAKE_LOCK)
    if (!raw) return null
    const parsed = UtilService.safeParseJSON<unknown>(raw, null)
    return isIntakeLock(parsed) ? parsed : null
  } catch {
    return null
  }
}

export const setIntakeLock = (input: { patientId: number; encounterUid: string | null }): void => {
  const lock: IntakeLock = { ...input, userId: getCurrentUserId() }
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEYS.INTAKE_LOCK, JSON.stringify(lock))
  } catch {
    // Storage can fail (private mode, quota); the in-memory block still applies.
  }
  emitChange()
}

export const clearIntakeLock = (): void => {
  removeStored()
  emitChange()
}

// The lock is bound to the staff session; a different/absent user must not inherit it.
export const getActiveIntakeLock = (): IntakeLock | null => {
  const lock = getIntakeLock()
  if (!lock) return null
  if (lock.userId !== getCurrentUserId()) {
    removeStored()
    return null
  }
  return lock
}

export const isIntakeLockActive = (): boolean => getActiveIntakeLock() !== null
